/** Attach the audio of an interview run outside the platform.
 *
 * Reported 2026-09-25: "Some controls cannot be completed due to
 * connection issues. In these cases, I conduct the interview by phone
 * using Quicktalk. I would need the option to upload the audio
 * recording directly to the client's profile."
 *
 * Filed as `external_audio`, never `recording`. The platform can vouch
 * for an egress it produced; it cannot vouch for what happened to a
 * phone recording before it arrived. The file is hashed client-side and
 * chained on arrival like any other evidence — what is NOT claimed is
 * anything about its life before the upload, and the label says so
 * rather than leaving the reader to assume.
 *
 * ## Why the button opens a drop zone and not the file dialog
 *
 * It used to call `input.click()` directly, which threw an OS dialog over
 * the drawer. That is the wrong shape for this job in two ways: a phone
 * recording has usually just been AirDropped or saved to the desktop, so
 * dragging it in is the shorter path; and a 200 MB upload needs somewhere
 * on the page to report itself. A modal dialog that closes the instant
 * you pick a file leaves nowhere for the progress to live.
 *
 * So the button reveals a drop target **in the same place**, which then
 * hosts the whole operation — drop or browse, progress, result. Browsing
 * is still one click away inside it, because not every file is draggable
 * from where it sits.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { describeApiError } from "@/lib/apiErrors";
import { type UploadProgress, uploadExternalAudio } from "@/lib/evidence";

/** Generous: a 60-minute phone interview at a low bitrate is ~30 MB,
 *  and an uncompressed one is far larger. Rejected client-side with a
 *  real message rather than failing at the PUT with a bucket error. */
const MAX_BYTES = 200 * 1024 * 1024;

const ACCEPT = "audio/*,.mp3,.m4a,.wav,.ogg,.aac,.amr,.opus,.3gp";

/** Extensions worth accepting even when the browser reports no MIME type.
 *  Phone recorders are the main offender — .amr and .m4a frequently
 *  arrive with an empty `type`, and refusing them for that would reject
 *  exactly the files this feature exists for. The server is the real
 *  gate; this only catches an obvious mis-drop early. */
const AUDIO_EXTS = [
  ".mp3", ".m4a", ".wav", ".ogg", ".aac", ".amr", ".opus", ".3gp", ".caf",
  ".wma", ".flac", ".aiff", ".mp4",
];

function looksLikeAudio(file: File): boolean {
  if (file.type.startsWith("audio/")) return true;
  // A phone "voice memo" is sometimes an mp4 container.
  if (file.type === "video/mp4" || file.type === "video/3gpp") return true;
  if (file.type === "") {
    const lower = file.name.toLowerCase();
    return AUDIO_EXTS.some((ext) => lower.endsWith(ext));
  }
  return false;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

type Rejection = "too_big" | "not_audio";

export function ExternalAudioUpload({
  sessionId,
  onUploaded,
}: {
  sessionId: string;
  onUploaded?: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<{
    reason: Rejection;
    name: string;
  } | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [pickedName, setPickedName] = useState<string>("");
  // Nested dragenter/dragleave on children would otherwise flicker the
  // highlight, so count depth instead of trusting a single boolean.
  const dragDepth = useRef(0);

  const upload = useMutation({
    mutationFn: (file: File) =>
      uploadExternalAudio({ sessionId, file, onProgress: setProgress }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["session-evidence", sessionId] });
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
      void qc.invalidateQueries({ queryKey: ["session-media", sessionId] });
      onUploaded?.();
    },
  });

  function accept(file: File | undefined) {
    if (!file) return;
    setRejected(null);
    upload.reset();
    if (!looksLikeAudio(file)) {
      setRejected({ reason: "not_audio", name: file.name });
      return;
    }
    if (file.size > MAX_BYTES) {
      setRejected({ reason: "too_big", name: file.name });
      return;
    }
    setPickedName(file.name);
    setProgress({ phase: "hashing", fraction: null });
    upload.mutate(file);
  }

  function endDrag() {
    dragDepth.current = 0;
    setDragging(false);
  }

  // Collapsed: just the button that reveals the zone.
  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={() => setOpen(true)}
        data-testid="external-audio-button"
      >
        {t("evidence.externalAudioButton")}
      </button>
    );
  }

  const busy = upload.isPending;
  const done = upload.isSuccess;
  const pct =
    progress?.phase === "uploading" && progress.fraction !== null
      ? Math.round(progress.fraction * 100)
      : null;

  // Both call sites sit in a wrapping row of small buttons, so the open
  // panel takes a whole line of its own (`basis-full`). `order-last` is
  // what keeps the rest of the row intact: without it the panel wraps
  // where the trigger stood, splitting its neighbours across three lines
  // — "Open" marooned above the zone, "Q&A" pushed below it. Sorted to
  // the end, the buttons stay on one line and the zone opens beneath
  // them. Collapsed, it is just another button in that row.
  return (
    <div
      className="order-last w-full basis-full space-y-2"
      data-testid="external-audio-panel"
    >
      <input
        ref={inputRef}
        type="file"
        // Some recorders hand back audio/* the browser won't name; the
        // server is the real gate, this is just the file picker's filter.
        accept={ACCEPT}
        className="hidden"
        data-testid="external-audio-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ""; // re-picking the same file must re-fire
          accept(file);
        }}
      />

      {/* The drop target doubles as the browse button, so the whole area
          is one affordance rather than a zone with a separate control
          hidden inside it. A <button> for keyboard reachability. */}
      <button
        type="button"
        disabled={busy}
        aria-describedby={`ext-audio-hint-${sessionId}`}
        className={`flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed px-3 py-4 text-center transition-colors ${
          dragging
            ? "border-nb-mint bg-nb-mint/10"
            : "border-slate-600 hover:border-slate-500 hover:bg-nb-surface-2"
        } ${busy ? "cursor-progress opacity-70" : "cursor-pointer"}`}
        onClick={() => !busy && inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          if (!busy) setDragging(true);
        }}
        onDragOver={(e) => {
          // Without this the browser navigates to the file instead.
          e.preventDefault();
          e.dataTransfer.dropEffect = busy ? "none" : "copy";
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) endDrag();
        }}
        onDrop={(e) => {
          e.preventDefault();
          endDrag();
          if (busy) return;
          accept(e.dataTransfer.files?.[0]);
        }}
        data-testid="external-audio-dropzone"
      >
        <span className="text-xs font-medium text-nb-text-2">
          {dragging
            ? t("evidence.externalAudioDropNow")
            : t("evidence.externalAudioDropPrompt")}
        </span>
        <span
          id={`ext-audio-hint-${sessionId}`}
          className="text-[11px] text-nb-text-3"
        >
          {t("evidence.externalAudioHint", {
            mb: Math.round(MAX_BYTES / 1024 / 1024),
          })}
        </span>
      </button>

      {/* ---- progress ---- */}
      {busy && progress && (
        <div
          className="space-y-1"
          data-testid="external-audio-progress"
          aria-live="polite"
        >
          <div className="flex items-baseline justify-between gap-2 text-[11px]">
            <span className="truncate text-nb-text-2" title={pickedName}>
              {pickedName}
            </span>
            <span className="shrink-0 font-mono tabular-nums text-nb-text-3">
              {pct === null
                ? t(
                    progress.phase === "hashing"
                      ? "evidence.externalAudioPreparing"
                      : "evidence.externalAudioFinalising",
                  )
                : `${pct}%`}
            </span>
          </div>
          <div
            className="h-1.5 overflow-hidden rounded-full bg-slate-700"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            {...(pct === null ? {} : { "aria-valuenow": pct })}
            aria-label={t("evidence.externalAudioUploading")}
          >
            {/* An unmeasurable phase gets a moving stripe, not a bar
                parked at a number it cannot justify. */}
            <div
              className={`h-full rounded-full bg-nb-mint ${
                pct === null ? "w-1/3 animate-pulse" : "transition-[width]"
              }`}
              {...(pct === null ? {} : { style: { width: `${pct}%` } })}
            />
          </div>
          {progress.phase === "uploading" &&
            progress.total !== undefined &&
            progress.loaded !== undefined && (
              <p className="text-right text-[10px] font-mono tabular-nums text-nb-text-3">
                {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
              </p>
            )}
        </div>
      )}

      {/* ---- result ---- */}
      {done && (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-nb-mint/40 bg-nb-mint/10 px-2.5 py-2"
          data-testid="external-audio-done"
          aria-live="polite"
        >
          <p className="min-w-0 text-[11px] text-nb-text-2">
            <span className="font-medium text-nb-mint">
              {t("evidence.externalAudioDone")}
            </span>{" "}
            <span className="block truncate text-nb-text-3" title={pickedName}>
              {pickedName}
            </span>
          </p>
          <button
            type="button"
            className="shrink-0 text-[11px] text-nb-mint hover:underline"
            onClick={() => {
              upload.reset();
              setProgress(null);
              setPickedName("");
            }}
            data-testid="external-audio-another"
          >
            {t("evidence.externalAudioAnother")}
          </button>
        </div>
      )}

      {rejected && (
        <p className="text-[11px] text-cvp-danger" data-testid="external-audio-rejected">
          {rejected.reason === "too_big"
            ? t("evidence.externalAudioTooBig", {
                mb: Math.round(MAX_BYTES / 1024 / 1024),
              })
            : t("evidence.externalAudioNotAudio", { name: rejected.name })}
        </p>
      )}

      {upload.isError && (
        <p className="text-[11px] text-cvp-danger">
          {describeApiError(upload.error, t("evidence.externalAudioFailed"))}
        </p>
      )}

      {!busy && !done && (
        <button
          type="button"
          className="text-[11px] text-nb-text-3 hover:text-nb-text-2"
          onClick={() => {
            setOpen(false);
            setRejected(null);
            upload.reset();
            setProgress(null);
          }}
          data-testid="external-audio-cancel"
        >
          {t("common.cancel")}
        </button>
      )}
    </div>
  );
}
